import * as React from 'react';
import { cn } from '@payment-plan/ui';
import { iniciales } from './logo-archivo';
import { useCompanyLogoBlob } from './use-companies';
import type { Company } from '../../../types/admin';

/** URL temporal de un `Blob`/`File` que se revoca al cambiar o desmontar (sin fugas de memoria). */
export function useObjectUrl(fuente: Blob | null | undefined): string | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!fuente || typeof URL.createObjectURL !== 'function') {
      setUrl(null);
      return undefined;
    }
    const creada = URL.createObjectURL(fuente);
    setUrl(creada);
    return () => URL.revokeObjectURL(creada);
  }, [fuente]);
  return url;
}

const TAMANOS = {
  sm: 'h-9 w-16',
  md: 'h-16 w-28',
} as const;

interface LogoMarcoProps {
  src: string | null;
  alt: string;
  nombre: string;
  size?: keyof typeof TAMANOS;
  className?: string;
}

/** Marco común del logo: la imagen contenida o, sin imagen, las iniciales. */
export function LogoMarco({ src, alt, nombre, size = 'sm', className }: LogoMarcoProps): React.JSX.Element {
  const marco = cn(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted',
    TAMANOS[size],
    className,
  );
  if (src) {
    return (
      <span className={marco}>
        <img src={src} alt={alt} className="h-full w-full object-contain p-1" />
      </span>
    );
  }
  return (
    <span role="img" aria-label={`${nombre} sin logo`} className={cn(marco, 'text-xs font-semibold text-muted-foreground')}>
      {iniciales(nombre)}
    </span>
  );
}

interface CompanyLogoProps {
  company: Pick<Company, 'id' | 'nombre' | 'logo'>;
  size?: keyof typeof TAMANOS;
  className?: string;
}

/**
 * feat-66 · Logo de una inmobiliaria pedido con la sesión (`GET /real-estate-companies/:id/logo`): el
 * back aplica el alcance y nunca expone una URL pública (AGENTS §14).
 */
export function CompanyLogo({ company, size, className }: CompanyLogoProps): React.JSX.Element {
  const { data } = useCompanyLogoBlob(company.id, company.logo?.sha256 ?? null);
  const src = useObjectUrl(company.logo ? data : null);
  return <LogoMarco src={src} alt={`Logo de ${company.nombre}`} nombre={company.nombre} size={size} className={className} />;
}
